import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Header } from './Header'
import { Stepper } from './Stepper'
import { PriceSummary } from './PriceSummary'
import { Button } from '../ui/Button'
import { useBooking } from '../../store/bookingStore'
import { formatCents } from '../../lib/money'
import type { Locale } from '../../lib/api/types'
import type { StepKey } from '../../lib/steps'

interface StepShellProps {
  stepKey: StepKey
  title: string
  subtitle?: string
  children: ReactNode
  showPrice?: boolean
  onBack?: () => void
  primaryLabel?: string
  onPrimary?: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
}

export function StepShell({
  stepKey,
  title,
  subtitle,
  children,
  showPrice = true,
  onBack,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  primaryLoading,
}: StepShellProps) {
  const { t, i18n } = useTranslation()
  const calc = useBooking((s) => s.calculatedInfo)
  const locale = i18n.language as Locale

  return (
    <div className="min-h-dvh bg-ink-50 pb-[calc(10rem_+_env(safe-area-inset-bottom))] lg:pb-12">
      <Header />

      <div className="border-b border-ink-100 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6 xl:max-w-6xl">
          <Stepper current={stepKey} />
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 pt-8 sm:px-6 lg:pt-10 xl:max-w-6xl">
        {/* two-column (content + price) only when a price sidebar is shown; otherwise full width */}
        <div
          className={
            showPrice
              ? 'grid grid-cols-1 gap-8 lg:grid-cols-[1fr_22rem] lg:gap-10 xl:grid-cols-[1fr_24rem] xl:gap-14'
              : 'grid grid-cols-1 gap-8'
          }
        >
          {/* every step spans the same width (the shared max-w-5xl/6xl main). No-price steps simply
              don't render the price sidebar; the Date step supplies its own calendar + Orario columns. */}
          <div className="animate-fade-up">
            <header className="mb-6 lg:mb-8">
              <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl lg:text-[34px]">
                {title}
              </h1>
              {subtitle && <p className="mt-1.5 text-ink-500 lg:text-lg">{subtitle}</p>}
            </header>
            {children}

            {/* desktop actions */}
            <div className="mt-8 hidden items-center justify-between lg:flex">
              {onBack ? (
                <Button variant="ghost" onClick={onBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
                  {t('common.back')}
                </Button>
              ) : (
                <span />
              )}
              {onPrimary && (
                <Button
                  size="lg"
                  onClick={onPrimary}
                  disabled={primaryDisabled}
                  loading={primaryLoading}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  {primaryLabel ?? t('common.continue')}
                </Button>
              )}
            </div>
          </div>

          {showPrice && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <PriceSummary />
              </div>
            </aside>
          )}
        </div>
      </main>

      {/* mobile sticky action bar — sits ABOVE the always-visible tab nav (no overlap) */}
      <div className="fixed inset-x-0 bottom-[calc(3.5rem_+_env(safe-area-inset-bottom))] z-30 border-t border-ink-100 bg-white/95 px-4 pt-3 pb-3 backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          {onBack && (
            <Button variant="outline" size="lg" onClick={onBack} className="flex-none px-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex flex-1 items-center justify-between gap-3">
            {showPrice && calc && (
              <div className="leading-tight">
                <p className="text-[11px] text-ink-400">{t('price.total')}</p>
                <p className="font-display text-lg font-extrabold text-ink-900">
                  {formatCents(calc.discountedAmount, locale)}
                </p>
              </div>
            )}
            {onPrimary && (
              <Button
                size="lg"
                onClick={onPrimary}
                disabled={primaryDisabled}
                loading={primaryLoading}
                className="flex-1"
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                {primaryLabel ?? t('common.continue')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
